/* eslint-disable react/no-array-index-key,no-console */
import * as React from 'react';
import { capitalCase } from 'change-case';
import styled from 'styled-components';

import { Loader, StatisticBlock } from '../atoms';
import { getFranchiseList } from '../api/franchise';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import DashboardLayout from '../layouts/DashboardLayout';
import {
  getFranchiseSales,
  getFranchiseMonthRevenue,
  getFranchiseIncome,
} from '../api/dashboard';
import FranchiseSalesGraphWidget from '../organisms/Dashboard/FranchiseSalesGraphWidget';
import FranchiseIncomeChartWidget from '../organisms/Dashboard/FranchiseIncomeChartWidget';

const DashboardPage = () => {
  const [isLoading, setIsLoading] = React.useState<boolean>(
    false,
  );

  const [
    franchisesData,
    setFranchisesData,
  ] = React.useState(null);

  const [
    franchiseMonthRevenueData,
    setFranchiseMonthRevenueData,
  ] = React.useState(null);

  const [
    franchiseSalesData,
    setFranchiseSalesData,
  ] = React.useState(null);

  const [
    franchiseIncomeData,
    setFranchiseIncomeData,
  ] = React.useState(null);

  const [
    franchiseSalesGraphSelected,
    setFranchiseSalesGraphSelected,
  ] = React.useState(null);

  const [
    franchiseIncomeChartSelected,
    setFranchiseIncomeChartSelected,
  ] = React.useState(null);

  React.useEffect(() => {
    if (franchiseSalesGraphSelected) {
      getFranchiseSales(franchiseSalesGraphSelected)
        .then(response => {
          const newResponse = response.data.map(el => ({
            ...el,
            projectedSales: Number(el.projectedSales) || 0,
            sales: Number(el.sales) || 0,
          }));
          return setFranchiseSalesData(newResponse);
        })
        .catch(e => console.error(e));
    }
  }, [franchiseSalesGraphSelected]);

  React.useEffect(() => {
    if (franchiseIncomeChartSelected) {
      getFranchiseIncome(franchiseIncomeChartSelected)
        .then(response => {
          const payload = response.data || {};
          setFranchiseIncomeData({
            ...payload,
            sales: Number(payload.sales) || 0,
            projectedSales:
              Number(payload.projectedSales) || 0,
            total: Number(payload.total) || 0,
          });
        })
        .catch(e => console.error(e));
    }
  }, [franchiseIncomeChartSelected]);

  React.useEffect(() => {
    setIsLoading(true);

    // Show all franchises on initial load
    setFranchiseIncomeChartSelected([]);
    setFranchiseSalesGraphSelected([]);

    Promise.all([
      getFranchiseList({
        ...GET_ALL_QUERY_PARAMS,
        ordering: 'name',
      }),
      getFranchiseMonthRevenue(),
    ])
      .then(([franchises, franchiseMonthRevenue]) => {
        setFranchisesData(franchises.data.results);

        const monthlyEarning =
          franchiseMonthRevenue.data.monthly * 0.1;

        setFranchiseMonthRevenueData({
          income: monthlyEarning * 0.7,
          marketing: monthlyEarning * 0.2,
          donation: monthlyEarning * 0.1,
          monthlyEarning,
          yearlyEarning: franchiseMonthRevenue.data.yearly,
        });
      })
      .catch(e => console.error(e))
      .finally(() => setIsLoading(false));
  }, []);

  const currentPeriodLabel = new Date().toLocaleString(
    'en-US',
    { month: 'long', year: 'numeric' },
  );

  return (
    <DashboardLayout>
      <PageContainer>
        <PageHeader>
          <div>
            <Title>Revenue overview</Title>
            <Subtitle>{currentPeriodLabel}</Subtitle>
          </div>
        </PageHeader>

        {isLoading ? (
          <CardShell>
            <LoaderContainer>
              <Loader size={26} />
            </LoaderContainer>
          </CardShell>
        ) : (
          franchiseMonthRevenueData && (
            <StatsGrid>
              {Object.entries(
                franchiseMonthRevenueData,
              ).map(([label, value], index) => {
                const formattedValue = new Intl.NumberFormat(
                  'en-US',
                  {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                  },
                ).format(+value);

                return (
                  <StatisticBlock
                    label={capitalCase(label)}
                    value={formattedValue}
                    key={index}
                  />
                );
              })}
            </StatsGrid>
          )
        )}

        {franchisesData && (
          <ChartsGrid>
            <CardShell>
              <FranchiseSalesGraphWidget
                data={franchiseSalesData}
                franchises={franchisesData}
                onSelectChange={(selected, isCanceled) =>
                  !isCanceled &&
                  setFranchiseSalesGraphSelected(
                    selected?.map(item => item.id),
                  )
                }
              />
            </CardShell>

            <CardShell>
              <FranchiseIncomeChartWidget
                data={franchiseIncomeData}
                franchises={franchisesData}
                onSelectChange={(selected, isCanceled) =>
                  !isCanceled &&
                  setFranchiseIncomeChartSelected(
                    selected?.map(item => item.id),
                  )
                }
              />
            </CardShell>
          </ChartsGrid>
        )}
      </PageContainer>
    </DashboardLayout>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  @media only screen and (max-width: 840px) {
    padding: 16px;
    gap: 16px;
  }

  @media only screen and (max-width: 540px) {
    padding: 12px;
    gap: 12px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.3;
`;

const Subtitle = styled.div`
  margin-top: 4px;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(200px, 1fr)
  );
  gap: 16px;

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  @media only screen and (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(420px, 1fr)
  );
  gap: 20px;
  align-items: stretch;

  @media only screen and (max-width: 1150px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const CardShell = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  box-sizing: border-box;

  @media only screen and (max-width: 840px) {
    padding: 18px;
  }

  @media only screen and (max-width: 540px) {
    padding: 14px;
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
`;

export default DashboardPage;
